/**
 * Shared PO management hook used by both Inventory and Cashflow pages.
 *
 * Owns all PO editing state (dialog open/close, field values, loading states)
 * but delegates actual save/delete API calls to page-provided callbacks.
 */

import { useRef, useState } from "react";
import moment from "moment";
import isEqual from "lodash/isEqual";
import { PurchaseOrder, POLineItem } from "models/InventoryModel";
import { Supplier } from "models/CashflowModel";

export interface POSaveParams {
  poNumber: string;
  arriveByDate: string;
  description: string;
  lineItems: POLineItem[];
  amount: number | null;
  supplierId: number | null;
  /** Tariff rate as percentage (e.g. 25 for 25%). Caller converts to decimal if needed. */
  tariffRatePercent: number | null;
  status: 'placed' | 'forecasted' | 'draft';
  brand: string | null;
  placementDate: string | null;
  etd: string | null;
  countryOfDestination: string | null;
  paymentDateOverrides: Record<string, string> | null;
  priceType: string | null;
  containerType: string | null;
  shipToFcId: number | null;
  /** True when this save creates a PO rather than editing one — the server
   *  then refuses a PO number that is already taken. */
  isNewPO: boolean;
  /** Original PO number (for rename detection) */
  originalPONumber: string;
  /** Original line items (for differential delete) */
  originalLineItems: POLineItem[];
  /** Called as soon as the server has renamed the PO, even if a later step of the save fails */
  onRenamed?: () => void;
}

export interface UsePOManagementOptions {
  inventoryOptions: Array<{ id: string; name: string | null; skus: string[] }>;
  suppliers: Supplier[];
  onSave: (params: POSaveParams) => Promise<void>;
  onDelete: (po: PurchaseOrder) => Promise<void>;
  /** Default date for new POs. Defaults to 1 week from today. */
  defaultNewPODate?: string;
}

export function usePOManagement({
  inventoryOptions,
  suppliers,
  onSave,
  onDelete,
  defaultNewPODate,
}: UsePOManagementOptions) {
  const getDefaultDate = () => defaultNewPODate || moment().add(1, "week").format("YYYY-MM-DD");

  // ── Dialog state ──
  const [isPODialogOpen, setIsPODialogOpen] = useState(false);
  const [editingPO, setEditingPO] = useState<PurchaseOrder | null>(null);
  const [isCreatingNewPO, setIsCreatingNewPO] = useState(false);
  const [poTabValue, setPOTabValue] = useState<"confirmed" | "draft" | "drafts" | "future" | "past" | "incomplete">("confirmed");

  // ── Editing fields ──
  const [editingPONumber, setEditingPONumber] = useState("");
  const [editingPODate, setEditingPODate] = useState(getDefaultDate());
  const [editingPODescription, setEditingPODescription] = useState("");
  const [editingPOLineItems, setEditingPOLineItems] = useState<POLineItem[]>([]);

  // ── Cashflow fields ──
  const [editingPOAmount, setEditingPOAmount] = useState<number | null>(null);
  const [editingPOSupplierId, setEditingPOSupplierId] = useState<number | null>(null);
  const [editingPOTariffRate, setEditingPOTariffRate] = useState<number | null>(null);
  const [editingPOStatus, setEditingPOStatus] = useState<'placed' | 'forecasted' | 'draft'>('placed');
  const [editingPOBrand, setEditingPOBrand] = useState<string | null>(null);
  const [editingPOPlacementDate, setEditingPOPlacementDate] = useState<string | null>(null);
  const [editingPOEtd, setEditingPOEtd] = useState<string | null>(null);
  const [editingPOCountryOfDestination, setEditingPOCountryOfDestination] = useState<string | null>(null);
  const [editingPOPaymentDateOverrides, setEditingPOPaymentDateOverrides] = useState<Record<string, string> | null>(null);
  const [editingPOPriceType, setEditingPOPriceType] = useState<string | null>(null);
  const [editingPOContainerType, setEditingPOContainerType] = useState<string | null>(null);
  const [editingPOShipToFcId, setEditingPOShipToFcId] = useState<number | null>(null);

  // ── Tracking for rename/diff ──
  const [originalPONumber, setOriginalPONumber] = useState("");
  const [originalPOLineItems, setOriginalPOLineItems] = useState<POLineItem[]>([]);

  // Which editor "session" is on screen: bumped whenever a different PO (or none) is opened. A
  // save that finishes after the person moved on must not write its PO into the editor's
  // "original" — that is how a rename of 266B deleted 266A CA (2026-09-21).
  const editorSession = useRef(0);
  const savingSnapshot = useRef<{ session: number; poNumber: string; lineItems: POLineItem[] } | null>(null);

  // ── Loading states ──
  const [isSavingPOs, setIsSavingPOs] = useState(false);
  const [isDeletingPO, setIsDeletingPO] = useState(false);
  const [poToDelete, setPOToDelete] = useState<PurchaseOrder | null>(null);

  // ── Dialog filter ──
  const [dialogInitialFilter, setDialogInitialFilter] = useState("");

  // ── Helpers ──
  const resetCashflowFields = () => {
    setEditingPOAmount(null);
    setEditingPOSupplierId(null);
    setEditingPOTariffRate(null);
    setEditingPOStatus('placed');
    setEditingPOBrand(null);
    setEditingPOPlacementDate(null);
    setEditingPOEtd(null);
    setEditingPOCountryOfDestination(null);
    setEditingPOPaymentDateOverrides(null);
    setEditingPOPriceType(null);
    setEditingPOContainerType(null);
    setEditingPOShipToFcId(null);
  };

  const resetEditingState = () => {
    editorSession.current += 1;
    setEditingPO(null);
    setIsCreatingNewPO(false);
    setEditingPONumber("");
    setOriginalPONumber("");
    setEditingPODate(getDefaultDate());
    setEditingPODescription("");
    setEditingPOLineItems([]);
    setOriginalPOLineItems([]);
    resetCashflowFields();
  };

  // ── Actions ──
  const openEditPO = (po: PurchaseOrder) => {
    editorSession.current += 1;
    setEditingPO(po);
    setIsCreatingNewPO(false);
    setDuplicatedFromPoNumber(null);
    setEditingPONumber(po.poNumber);
    setOriginalPONumber(po.poNumber);
    setEditingPODate(po.arriveByDate);
    setEditingPODescription(po.description);
    setEditingPOLineItems([...po.lineItems]);
    setOriginalPOLineItems([...po.lineItems]);
    // Populate cashflow fields
    setEditingPOAmount(po.amount ?? null);
    setEditingPOSupplierId(po.supplierId ?? null);
    setEditingPOTariffRate(po.tariffRate != null ? parseFloat((po.tariffRate * 100).toFixed(2)) : null);
    setEditingPOStatus(po.status || 'placed');
    setEditingPOBrand(po.brand || null);
    setEditingPOPlacementDate(po.placementDate || null);
    setEditingPOEtd(po.etd || null);
    setEditingPOCountryOfDestination(po.countryOfDestination || null);
    setEditingPOPaymentDateOverrides(po.paymentDateOverrides || null);
    setEditingPOPriceType(po.priceType || null);
    setEditingPOContainerType(po.containerType || null);
    setEditingPOShipToFcId(po.shipToFcId ?? null);
  };

  // The PO a new one was started from with Duplicate (shown in the editor); null otherwise
  const [duplicatedFromPoNumber, setDuplicatedFromPoNumber] = useState<string | null>(null);

  const startCreatePO = (prefilledInventoryId?: string) => {
    editorSession.current += 1;
    setEditingPO(null);
    setIsCreatingNewPO(true);
    setDuplicatedFromPoNumber(null);
    setEditingPONumber("");
    setOriginalPONumber("");
    setEditingPODescription("");
    setOriginalPOLineItems([]);
    setEditingPOLineItems([{
      inventoryId: prefilledInventoryId || "",
      inventoryName: prefilledInventoryId
        ? inventoryOptions.find(opt => opt.id === prefilledInventoryId)?.name || undefined
        : undefined,
      quantity: 0,
    }]);
    resetCashflowFields();
  };

  // A PO read off its PDF: same blank form as Create New PO, pre-filled with
  // the PDF's number, date and matched lines. Container, ship-to and tariff are
  // still the person's to fill in (or it can be saved as a draft).
  const startCreatePOFromPdf = ({ poNumber, poDate, lineItems }: { poNumber: string | null; poDate: string | null; lineItems: POLineItem[] }) => {
    startCreatePO();
    setEditingPONumber(poNumber ?? "");
    setEditingPOPlacementDate(poDate ?? null);
    setEditingPOLineItems(lineItems);
  };

  /**
   * A new PO that starts as a copy of an existing one: its lines (item, quantity, price, product
   * number, tariff), supplier, container, ship-to, country, brand, price type and description.
   * Not copied: the number (suggest a new one), dates, payment overrides, and anything tying the
   * source to its WRO, NetSuite record or receipts. Starts as a draft.
   */
  const startDuplicatePO = (source: PurchaseOrder) => {
    startCreatePO();
    setDuplicatedFromPoNumber(source.poNumber);
    setEditingPODescription(source.description || "");
    setEditingPOLineItems(source.lineItems.map(line => ({
      inventoryId: line.inventoryId,
      inventoryName: line.inventoryName,
      sku: line.sku,
      quantity: line.quantity,
      unitPrice: line.unitPrice ?? null,
      tariffRate: line.tariffRate ?? null,
      manufacturerProductNumber: line.manufacturerProductNumber ?? null,
    })));
    setEditingPOSupplierId(source.supplierId ?? null);
    setEditingPOTariffRate(source.tariffRate != null ? parseFloat((source.tariffRate * 100).toFixed(2)) : null);
    setEditingPOStatus('draft');
    setEditingPOBrand(source.brand || null);
    setEditingPOCountryOfDestination(source.countryOfDestination || null);
    setEditingPOPriceType(source.priceType || null);
    setEditingPOContainerType(source.containerType || null);
    setEditingPOShipToFcId(source.shipToFcId ?? null);
  };

  /**
   * Duplicate from inside the editor: the copy takes what is on screen. Unsaved edits to the open PO
   * then live only in the copy, so ask before leaving the original without them.
   */
  const duplicateOpenPO = () => {
    if (!editingPO) return;
    const hasUnsavedEdits = editingPONumber !== originalPONumber
      || editingPODate !== editingPO.arriveByDate
      || editingPODescription !== (editingPO.description ?? "")
      || editingPOStatus !== (editingPO.status || 'placed')
      || editingPOContainerType !== (editingPO.containerType || null)
      || editingPOShipToFcId !== (editingPO.shipToFcId ?? null)
      || !isEqual(editingPOLineItems, originalPOLineItems);
    if (hasUnsavedEdits && !window.confirm(
      `${originalPONumber} has unsaved changes. The copy will include them, but ${originalPONumber} itself stays as last saved. Continue?`
    )) return;
    startDuplicatePO({
      ...editingPO,
      poNumber: originalPONumber,
      description: editingPODescription,
      lineItems: editingPOLineItems,
      supplierId: editingPOSupplierId,
      tariffRate: editingPOTariffRate != null ? editingPOTariffRate / 100 : null,
      brand: editingPOBrand,
      countryOfDestination: editingPOCountryOfDestination,
      priceType: editingPOPriceType,
      containerType: editingPOContainerType,
      shipToFcId: editingPOShipToFcId,
    } as PurchaseOrder);
  };

  // After a successful save: stay in the editor. The saved values become the
  // new "original" (so the next save diffs against them) and a freshly created
  // PO becomes an existing one, which turns on the existing-PO actions.
  // Uses what was SENT (the snapshot savePO took), and does nothing if the
  // editor has since moved to another PO or closed.
  const markSaved = () => {
    const saved = savingSnapshot.current;
    if (!saved || saved.session !== editorSession.current) return;
    setIsCreatingNewPO(false);
    setDuplicatedFromPoNumber(null);
    setOriginalPONumber(saved.poNumber);
    setOriginalPOLineItems([...saved.lineItems]);
    setEditingPO(prev => ({
      ...(prev ?? {}),
      poNumber: saved.poNumber,
      arriveByDate: editingPODate,
      description: editingPODescription,
      lineItems: [...saved.lineItems],
      amount: editingPOAmount,
      status: editingPOStatus,
      containerType: editingPOContainerType,
      shipToFcId: editingPOShipToFcId,
    }));
  };

  // The server renamed the PO but a later step of the save failed: the new number becomes the
  // editor's "original" so a retry doesn't rename the old number again. Line items stay as they
  // were, so the retry still removes the dropped lines.
  const markRenamed = (saved: { session: number; poNumber: string }) => {
    if (saved.session !== editorSession.current) return;
    setOriginalPONumber(saved.poNumber);
    setEditingPO(prev => (prev ? { ...prev, poNumber: saved.poNumber } : prev));
  };

  const cancelEdit = () => {
    editorSession.current += 1;
    setEditingPO(null);
    setIsCreatingNewPO(false);
    setDuplicatedFromPoNumber(null);
    setEditingPONumber("");
    setOriginalPONumber("");
    setEditingPOLineItems([]);
    setOriginalPOLineItems([]);
    resetCashflowFields();
  };

  const openDialog = (po?: PurchaseOrder) => {
    if (po) {
      openEditPO(po);
    } else {
      resetEditingState();
    }
    setIsPODialogOpen(true);
  };

  const closeDialog = () => {
    setDialogInitialFilter("");
    setIsPODialogOpen(false);
    resetEditingState();
  };

  const openDialogWithFilter = (filterText: string) => {
    setDialogInitialFilter(filterText);
    openDialog();
  };

  const savePO = async () => {
    // The server trims PO numbers, so the editor must remember the trimmed one
    const poNumber = editingPONumber.trim();
    if (!poNumber || editingPOLineItems.length === 0) return;
    if (poNumber !== editingPONumber) setEditingPONumber(poNumber);

    setIsSavingPOs(true);
    const snapshot = { session: editorSession.current, poNumber, lineItems: editingPOLineItems };
    savingSnapshot.current = snapshot;
    try {
      await onSave({
        poNumber,
        arriveByDate: editingPODate,
        description: editingPODescription,
        lineItems: editingPOLineItems,
        amount: editingPOAmount,
        supplierId: editingPOSupplierId,
        tariffRatePercent: editingPOTariffRate,
        status: editingPOStatus,
        brand: editingPOBrand,
        placementDate: editingPOPlacementDate,
        etd: editingPOEtd,
        countryOfDestination: editingPOCountryOfDestination,
        paymentDateOverrides: editingPOPaymentDateOverrides,
        priceType: editingPOPriceType,
        containerType: editingPOContainerType,
        shipToFcId: editingPOShipToFcId,
        isNewPO: isCreatingNewPO,
        originalPONumber,
        originalLineItems: originalPOLineItems,
        onRenamed: () => markRenamed(snapshot),
      });
    } finally {
      setIsSavingPOs(false);
    }
  };

  const requestDeletePO = (po: PurchaseOrder) => {
    setPOToDelete(po);
  };

  const confirmDeletePO = async () => {
    if (!poToDelete) return;

    setIsDeletingPO(true);
    try {
      await onDelete(poToDelete);
      setPOToDelete(null);
    } finally {
      setIsDeletingPO(false);
    }
  };

  const cancelDeletePO = () => {
    setPOToDelete(null);
  };

  // ── Line item helpers ──
  const addLineItem = () => {
    setEditingPOLineItems(prev => [...prev, { inventoryId: "", quantity: 0 }]);
  };

  const updateLineItem = (index: number, updates: Partial<POLineItem>) => {
    setEditingPOLineItems(prev =>
      prev.map((li, i) => (i === index ? { ...li, ...updates } : li))
    );
  };

  const removeLineItem = (index: number) => {
    setEditingPOLineItems(prev => prev.filter((_, i) => i !== index));
  };

  // ── Convenience: props bundle for <POManagementDialog> ──
  const dialogProps = {
    isOpen: isPODialogOpen,
    onClose: closeDialog,
    editingPO,
    isCreatingNewPO,
    editingPONumber,
    editingPODate,
    editingPODescription,
    editingPOLineItems,
    poTabValue,
    isSavingPOs,
    isDeletingPO,
    initialFilterText: dialogInitialFilter,
    inventoryOptions,
    onOpenEditPO: openEditPO,
    onStartCreatePO: startCreatePO,
    onStartCreatePOFromPdf: startCreatePOFromPdf,
    onDuplicatePO: startDuplicatePO,
    onDuplicateOpenPO: duplicateOpenPO,
    duplicatedFromPoNumber,
    onCancelEdit: cancelEdit,
    onSavePO: savePO,
    onDeletePO: requestDeletePO,
    onSetPONumber: setEditingPONumber,
    onSetPODate: setEditingPODate,
    onSetPODescription: setEditingPODescription,
    onAddLineItem: addLineItem,
    onUpdateLineItem: updateLineItem,
    onRemoveLineItem: removeLineItem,
    onReplaceLineItems: setEditingPOLineItems,
    onSetPOTabValue: setPOTabValue,
    suppliers,
    editingPOAmount,
    editingPOSupplierId,
    editingPOTariffRate,
    editingPOStatus,
    editingPOBrand,
    editingPOPlacementDate,
    editingPOEtd,
    onSetPOAmount: setEditingPOAmount,
    onSetPOSupplierId: setEditingPOSupplierId,
    onSetPOTariffRate: setEditingPOTariffRate,
    onSetPOStatus: setEditingPOStatus,
    onSetPOBrand: setEditingPOBrand,
    onSetPOPlacementDate: setEditingPOPlacementDate,
    onSetPOEtd: setEditingPOEtd,
    editingPOCountryOfDestination,
    editingPOPaymentDateOverrides,
    onSetPOCountryOfDestination: setEditingPOCountryOfDestination,
    onSetPOPaymentDateOverrides: setEditingPOPaymentDateOverrides,
    editingPOPriceType,
    editingPOContainerType,
    editingPOShipToFcId,
    onSetPOPriceType: setEditingPOPriceType,
    onSetPOContainerType: setEditingPOContainerType,
    onSetPOShipToFcId: setEditingPOShipToFcId,
  };

  return {
    // Dialog control
    isPODialogOpen,
    openDialog,
    closeDialog,
    openDialogWithFilter,

    // Editing state (read-only access for pages that need it)
    editingPO,
    isCreatingNewPO,
    editingPONumber,
    editingPODate,
    editingPODescription,
    editingPOLineItems,
    editingPOAmount,
    editingPOSupplierId,
    editingPOTariffRate,
    editingPOStatus,
    editingPOBrand,
    editingPOPlacementDate,
    editingPOEtd,
    editingPOCountryOfDestination,
    editingPOPaymentDateOverrides,
    editingPOPriceType,
    editingPOContainerType,
    originalPONumber,
    originalPOLineItems,

    // Tab
    poTabValue,
    setPOTabValue,

    // Actions
    openEditPO,
    startCreatePO,
    startDuplicatePO,
    cancelEdit,
    savePO,

    // Delete flow
    poToDelete,
    isDeletingPO,
    requestDeletePO,
    confirmDeletePO,
    cancelDeletePO,

    // Loading
    isSavingPOs,

    // Line item helpers
    addLineItem,
    updateLineItem,
    removeLineItem,

    // Dialog initial filter
    dialogInitialFilter,
    setDialogInitialFilter,

    // Convenience bundle
    dialogProps,
    markSaved,
  };
}
